import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ArrowRight } from "lucide-react";
import { api, OFFICIAL_BOOKING_URL } from "@/lib/data";
import RoomPreviewMedia from "@/components/RoomPreviewMedia";

export default function RoomDetail() {
    const { slug } = useParams();
    const [room, setRoom] = useState(null);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        api.get(`/rooms/${slug}`).then(({ data }) => setRoom(data)).catch(() => {});
        api.get(`/rooms`).then(({ data }) => setRelated(data.filter((r) => r.slug !== slug).slice(0, 3))).catch(() => {});
    }, [slug]);

    if (!room) return <div className="pt-40 max-w-5xl mx-auto px-6 text-navy/60">Loading…</div>;

    return (
        <div data-testid={`room-detail-${slug}`}>
            <div className="relative h-[85vh] min-h-[640px] w-full overflow-hidden">
                <RoomPreviewMedia image={room.hero_image} video={room.hero_video} alt={room.name} className="absolute inset-0 w-full h-full animate-ken-burns" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/20 to-navy/40 z-10" />
                <div className="relative z-20 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-14">
                    <Link to="/rooms" className="text-ivory/80 text-xs uppercase tracking-[0.24em] inline-flex items-center gap-2 hover:text-gold"><ArrowLeft size={14} /> All rooms</Link>
                    <div className="mt-4 text-[11px] uppercase tracking-[0.28em] text-gold">{room.view}</div>
                    <h1 className="font-display text-5xl md:text-7xl text-ivory mt-3">{room.name}</h1>
                    <p className="font-serif-italic text-2xl text-ivory/85 mt-3 max-w-2xl">{room.tagline}</p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-12">
                <div className="md:col-span-2 space-y-8">
                    <p className="text-navy/80 text-lg leading-relaxed">{room.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-y border-navy/10 py-6">
                        {[["Guests", room.occupancy], ["Beds", room.beds], ["Bathrooms", room.bathrooms], ["View", room.view]].map(([l, v]) => (
                            <div key={l}><div className="text-[10px] uppercase tracking-[0.18em] text-navy/50">{l}</div><div className="mt-1 font-display text-lg text-navy">{v}</div></div>
                        ))}
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-4">In this suite</div>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {room.features.map((f) => (
                                <li key={f} className="flex items-start gap-3 text-navy/80 text-sm"><Check size={16} className="text-gold mt-0.5" /> {f}</li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-4">Rooms in focus</div>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { label: "The Bath", src: room.bathroom_image },
                                { label: "The Terrace", src: room.balcony_image },
                                { label: "Lifestyle", src: room.lifestyle_image },
                                { label: "Details", src: room.detail_image },
                            ].filter((x) => x.src).map((x) => (
                                <figure key={x.label} className="relative rounded-xl overflow-hidden aspect-[4/3] group">
                                    <img src={x.src} alt={`${room.name} · ${x.label}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                                    <figcaption className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.24em] text-ivory bg-navy/60 px-2.5 py-1 rounded-full">{x.label}</figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </div>
                <aside className="md:sticky md:top-28 h-fit bg-white rounded-xl p-6 border border-navy/10 shadow-lg shadow-navy/10">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Starting From</div>
                    <div className="mt-1">
                        <span className="font-display text-4xl text-navy">${Math.min(...(room.rates && room.rates.length ? room.rates.map(r=>r.amount) : [room.price_per_night])).toFixed(0)}</span>
                        <span className="text-navy/50 text-sm"> USD / night</span>
                    </div>
                    {room.rates && room.rates.length > 1 && (
                        <div className="mt-3 pt-3 border-t border-navy/10 space-y-1.5">
                            {room.rates.slice().sort((a,b)=>a.amount-b.amount).slice(1).map((rt) => (
                                <div key={rt.label} className="flex items-center justify-between text-sm">
                                    <span className="text-navy/60">{rt.label}</span>
                                    <span className="text-navy font-medium">${rt.amount.toFixed(0)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-xs text-navy/60 mt-4">Official pricing and availability are confirmed through the resort's booking engine.</p>
                    <Link to="/booking" data-testid="room-detail-book" className="mt-6 btn-gold w-full text-center block py-3 rounded-full">Reserve Now</Link>
                    <Link to="/contact" className="mt-3 block text-center text-sm text-navy hover:text-gold">Speak to a concierge</Link>
                </aside>
            </div>

            {related.length > 0 && (
                <section className="bg-sand/25 py-20">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-4">You may also love</div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {related.map((r) => (
                                <Link to={`/rooms/${r.slug}`} key={r.id} className="group bg-white rounded-xl overflow-hidden hover-lift">
                                    <div className="aspect-[4/3] overflow-hidden"><img src={r.hero_image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" /></div>
                                    <div className="p-5 flex items-center justify-between">
                                        <div><div className="font-display text-lg text-navy">{r.name}</div><div className="text-xs text-navy/60">{r.view}</div></div>
                                        <ArrowRight size={16} className="text-gold" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
